import type {
  LocationDetail,
  LoyaltyBusiness,
  LoyaltyInfo,
} from "../contexts/CustomerContext";

export interface BusinessGroup {
  business: LoyaltyBusiness;
  /** The shared loyalty — the same at every location of the business. */
  loyalty: LoyaltyInfo;
  /** Any one of the business's locations, for routes keyed by location. */
  loyaltyLocation: LocationDetail;
  locations: LocationDetail[];
}

export const findLoyaltyBusiness = (
  loyalty: LoyaltyInfo | null | undefined,
  businesses: LoyaltyBusiness[],
): LoyaltyBusiness | null =>
  loyalty?.sharedBusinessId
    ? (businesses.find((b) => b.businessId === loyalty.sharedBusinessId) ??
      null)
    : null;

/**
 * Splits locations into one group per shared-loyalty business plus the
 * standalone rest, keeping the incoming order (groups appear in the order of
 * their first location).
 */
export const groupLocationsByBusiness = (
  locations: LocationDetail[],
  businesses: LoyaltyBusiness[],
): { groups: BusinessGroup[]; standalone: LocationDetail[] } => {
  const groups = new Map<string, BusinessGroup>();
  const standalone: LocationDetail[] = [];

  for (const location of locations) {
    const { loyalty } = location;
    const business = findLoyaltyBusiness(loyalty, businesses);
    if (!loyalty || !business) {
      standalone.push(location);
      continue;
    }
    const group = groups.get(business.businessId);
    if (group) group.locations.push(location);
    else
      groups.set(business.businessId, {
        business,
        loyalty,
        loyaltyLocation: location,
        locations: [location],
      });
  }

  return { groups: Array.from(groups.values()), standalone };
};

export const rewardsReadyCount = (location: LocationDetail) =>
  (location.punchCards ?? []).reduce((n, c) => n + c.availableRewards, 0);

export const sharedReachText = ({ name, locationCount }: LoyaltyBusiness) =>
  locationCount > 1
    ? `Your points work at all ${locationCount} ${name} locations`
    : `Your points work at every ${name} location`;

/** The short form, for labels next to the business's name: "all 3 locations". */
export const sharedLocationsText = ({ locationCount }: LoyaltyBusiness) =>
  locationCount > 1 ? `all ${locationCount} locations` : "all locations";
